<script setup>
import { computed, nextTick, ref } from 'vue';
import { OnClickOutside } from '@vueuse/components';
import { useI18n } from 'vue-i18n';
import Button from 'dashboard/components-next/button/Button.vue';

// Mesmo visual do ComboBox, com opções agrupadas e itens desabilitados.
// groups: [{ id, label, options: [{ value, label, disabled?, hint? }] }]
const props = defineProps({
  groups: { type: Array, required: true },
  modelValue: { type: String, default: '' },
  placeholder: { type: String, default: '' },
  hasError: { type: Boolean, default: false },
});

const emit = defineEmits(['update:modelValue']);

const { t } = useI18n();

const open = ref(false);
const search = ref('');
const searchInput = ref(null);

const selectedLabel = computed(
  () =>
    props.groups
      .flatMap(({ options }) => options)
      .find(({ value }) => value === props.modelValue)?.label
);

const filteredGroups = computed(() => {
  const term = search.value.trim().toLowerCase();
  return props.groups
    .map(group => ({
      ...group,
      options: group.options.filter(({ label }) =>
        label.toLowerCase().includes(term)
      ),
    }))
    .filter(({ options }) => options.length);
});

const toggle = () => {
  open.value = !open.value;
  if (!open.value) return;
  search.value = '';
  nextTick(() => searchInput.value?.focus());
};

const select = option => {
  if (option.disabled) return;
  emit('update:modelValue', option.value);
  open.value = false;
};
</script>

<template>
  <div class="relative w-full min-w-0">
    <OnClickOutside @trigger="open = false">
      <Button
        variant="outline"
        :color="hasError && !open ? 'ruby' : open ? 'blue' : 'slate'"
        :label="selectedLabel || placeholder"
        trailing-icon
        no-animation
        class="justify-between w-full !px-3 !h-8 !py-1 text-sm font-normal bg-n-alpha-black2 hover:enabled:!bg-n-alpha-black1 focus:outline-n-brand"
        :class="{
          '!text-n-slate-10': !selectedLabel,
          '!text-n-slate-12': selectedLabel,
          focused: open,
          '[&:not(.focused)]:dark:outline-n-weak [&:not(.focused)]:hover:enabled:!outline-n-brand':
            !hasError,
        }"
        :icon="open ? 'i-lucide-chevron-up' : 'i-lucide-chevron-down'"
        @click="toggle"
      />

      <div
        v-show="open"
        class="absolute z-50 w-full mt-1 border rounded-md shadow-lg bg-n-solid-1 border-n-strong"
      >
        <div class="relative border-b border-n-strong">
          <span
            class="absolute i-lucide-search top-2.5 size-4 left-3 text-n-slate-11"
          />
          <input
            ref="searchInput"
            v-model="search"
            type="search"
            :placeholder="t('COMBOBOX.SEARCH_PLACEHOLDER')"
            class="reset-base w-full py-2 pl-10 pr-2 text-sm focus:outline-none border-none rounded-t-md bg-n-solid-1 text-n-slate-12"
          />
        </div>
        <ul class="py-1 mb-0 overflow-y-auto max-h-60" role="listbox">
          <template v-for="group in filteredGroups" :key="group.id">
            <li
              class="px-3 pt-2 pb-1 text-xs font-medium uppercase text-n-slate-11"
            >
              {{ group.label }}
            </li>
            <li
              v-for="option in group.options"
              :key="option.value"
              role="option"
              :aria-selected="option.value === modelValue"
              :aria-disabled="option.disabled"
              :title="option.hint"
              class="flex items-center justify-between w-full gap-2 px-3 py-2 text-sm transition-colors duration-150"
              :class="[
                option.disabled
                  ? 'cursor-not-allowed text-n-slate-10'
                  : 'cursor-pointer hover:bg-n-alpha-2 text-n-slate-12',
                { 'bg-n-alpha-2': option.value === modelValue },
              ]"
              @click="select(option)"
            >
              <span class="truncate">{{ option.label }}</span>
              <span
                v-if="option.value === modelValue"
                class="flex-shrink-0 i-lucide-check size-4 text-n-slate-11"
              />
            </li>
          </template>
          <li
            v-if="!filteredGroups.length"
            class="px-3 py-2 text-sm text-n-slate-11"
          >
            {{ t('COMBOBOX.EMPTY_STATE') }}
          </li>
        </ul>
      </div>
    </OnClickOutside>
  </div>
</template>
